import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import client from "@/lib/api";
import { SectionHeader, EmptyState, notifyError, notifyOk } from "./_ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const empty = { telegram_id: "", name: "", role: "manager", active: true };

export default function Managers() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = () => client.get("/admin/managers").then((r) => setItems(r.data)).catch(notifyError);
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (m) => { setForm({ ...m }); setEditing(m.id); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await client.put(`/admin/managers/${editing}`, form);
      else await client.post("/admin/managers", form);
      setOpen(false); notifyOk("Сохранено"); load();
    } catch (e) { notifyError(e); }
  };
  const remove = async (id) => {
    if (!window.confirm("Удалить менеджера?")) return;
    await client.delete(`/admin/managers/${id}`); load();
  };

  return (
    <div>
      <SectionHeader
        title="Менеджеры"
        description="Telegram ID пользователей, которым разрешён доступ к боту."
        action={
          <Button data-testid="manager-add-btn" onClick={openNew} style={{ background: "var(--ci-accent)", color: "#1a1a1a" }}>
            <Plus className="w-4 h-4 mr-1.5" /> Добавить
          </Button>
        }
      />

      {items.length === 0 ? (
        <EmptyState text="Менеджеров пока нет. Добавьте Telegram ID для доступа к боту." />
      ) : (
        <div className="border border-neutral-200 rounded-lg bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Telegram ID</TableHead>
                <TableHead>Имя</TableHead>
                <TableHead>Роль</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead className="text-right">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((m) => (
                <TableRow key={m.id} data-testid={`manager-row-${m.id}`}>
                  <TableCell className="font-mono text-xs">{m.telegram_id}</TableCell>
                  <TableCell>{m.name}</TableCell>
                  <TableCell>{m.role}</TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${m.active ? "bg-green-100 text-green-700" : "bg-neutral-200 text-neutral-600"}`}>
                      {m.active ? "активен" : "выключен"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <button onClick={() => openEdit(m)} className="inline-flex p-1.5 text-neutral-600 hover:text-black" data-testid={`manager-edit-${m.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(m.id)} className="inline-flex p-1.5 text-red-600" data-testid={`manager-delete-${m.id}`}><Trash2 className="w-4 h-4" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>{editing ? "Редактировать менеджера" : "Новый менеджер"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Telegram ID</Label>
              <Input data-testid="manager-tgid-input" value={form.telegram_id} onChange={(e) => setForm({ ...form, telegram_id: e.target.value })} className="border-neutral-300 font-mono" placeholder="напр. 123456789" />
            </div>
            <div className="space-y-1.5">
              <Label>Имя</Label>
              <Input data-testid="manager-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-neutral-300" />
            </div>
            <div className="space-y-1.5">
              <Label>Роль</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="manager-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Менеджер</SelectItem>
                  <SelectItem value="admin">Администратор</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <Switch data-testid="manager-active-switch" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <Label>Активен</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Отмена</Button>
            <Button data-testid="manager-save-btn" onClick={save} style={{ background: "var(--ci-accent)", color: "#1a1a1a" }}>Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
